import React, {
  useRef,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import { Container, Graphics, Text, Sprite } from "@pixi/react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import * as PIXI from "pixi.js";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import {
  closeAutoplayPopup,
  openErrorPopup,
  startAutoPlay,
  stopAutoPlay,
  setAutoSpinCount,
  setAutoplaySpeed,
  setPendingAutoplaySpeed,
  closeMenu,
} from "../../../../State/actions";
import {
  selectAutoplayPopupActive,
  selectAutoSpinCount,
  selectAutoPlayActive,
  selectAutoPlaySpeed,
  selectPendingAutoPlaySpeed,
  selectIsSpinning,
} from "../../../../State/selectors";
import { PixiSettings } from "../../../../pixiSettings";
import { FONT_CONFIG } from "../../../../textureConfigs";
import { getUITexture } from "../../../../assets";
import PopupButton from "../Buttons/PopupButton";
import anime from "animejs";
import { SOUND_NAME } from "../../../../sounds";
import { soundManager } from "../../../../SoundManager";
import store from "../../../../State/Store/store";
import { checkBalanceAndBetStatus } from "../../../../utilities";

// Overlay settings
const OVERLAY_BACKGROUND_ALPHA = 0.6;
const OVERLAY_BACKGROUND_COLOR = 0x090f41;
const OVERLAY_FADE_DURATION = 200;

// Popup animation settings
const POPUP_ANIM_DURATION = 250;

// Autoplay count options
const AUTOPLAY_OPTIONS = [10, 25, 50, 75, 100, Infinity];

// Speed options
const SPEED_OPTIONS = [
  { key: "auto", labelKey: "spin_speed_auto" },
  { key: "quick", labelKey: "spin_speed_quick" },
  { key: "turbo", labelKey: "spin_speed_turbo" },
];

// Popup configuration — same sizing as BetSelectPopup
const AUTOPLAY_POPUP_CONFIG = {
  basePadding: 20,
  baseTitleFontSize: 28,
  baseContentWidth: 380,
  titleBarRatio: 0.13,
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { scaleMultiplier: 0.75 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 0.6 },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { scaleMultiplier: 0.8 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 0.7 },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 1.0 },
    },
  },
};

const AutoplayPopup = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const popupActive = useSelector(selectAutoplayPopupActive);
  const autoSpinCount = useSelector(selectAutoSpinCount);
  const autoPlayActive = useSelector(selectAutoPlayActive);
  const autoPlaySpeed = useSelector(selectAutoPlaySpeed);
  const pendingAutoPlaySpeed = useSelector(selectPendingAutoPlaySpeed);
  const isSpinning = useSelector(selectIsSpinning);
  // const spinComplete = useSelector(selectSpinComplete);

  // Effective speed (pending takes precedence)
  const currentSpeed = pendingAutoPlaySpeed || autoPlaySpeed;

  const { dimensions, device, orientation } = useDimensions();

  const popupContentRef = useRef(null);

  const [overlayAlpha, setOverlayAlpha] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);

  const isOpen = popupActive;

  const handleClose = useCallback(() => {
    if (isClosing) return;
    soundManager.play(SOUND_NAME.button_click);
    setIsClosing(true);
  }, [isClosing]);

  const handleAutoSpinCountSelect = useCallback(
    (option) => {
      if (autoPlayActive) return;
      soundManager.play(SOUND_NAME.button_click);
      dispatch(setAutoSpinCount(option));
    },
    [dispatch, autoPlayActive]
  );

  const handleStartAutoplay = useCallback(() => {
    soundManager.play(SOUND_NAME.button_click);

    // Balance check
    const currentState = store.getState();
    const balanceStatus = checkBalanceAndBetStatus(
      currentState.game.balance,
      currentState.game.bet,
      currentState.game.bets
    );

    if (balanceStatus.action === "insufficient_credits") {
      dispatch(openErrorPopup(301));
      dispatch(closeAutoplayPopup());
      return;
    }

    dispatch(startAutoPlay());
    setIsClosing(true);
  }, [dispatch]);

  const handleStopAutoplay = useCallback(() => {
    soundManager.play(SOUND_NAME.button_click);
    dispatch(stopAutoPlay());
    setIsClosing(true);
  }, [dispatch]);

  // Speed change handler with guards from ButtonTurbo
  const handleSpeedChange = useCallback(
    (newSpeed) => {
      if (newSpeed === currentSpeed) return;
      soundManager.play(SOUND_NAME.button_click);

      // If spinning or autoplay is active, set as pending
      if (isSpinning || autoPlayActive) {
        dispatch(setPendingAutoplaySpeed(newSpeed));
      } else {
        // Apply immediately if no spin is active
        dispatch(setAutoplaySpeed(newSpeed));
      }
    },
    [dispatch, currentSpeed, isSpinning, autoPlayActive]
  );

  // Determine action button behavior based on state
  const actionButtonText = useMemo(() => {
    if (autoPlayActive) return t("stop");
    return t("autoplay_spin", { ns: "common" });
  }, [autoPlayActive, t]);

  const handleActionClick = useCallback(() => {
    if (autoPlayActive) {
      handleStopAutoplay();
    } else {
      handleStartAutoplay();
    }
  }, [autoPlayActive, handleStopAutoplay, handleStartAutoplay]);

  // Close menu when popup opens
  useEffect(() => {
    if (popupActive) {
      dispatch(closeMenu());
    }
  }, [popupActive, dispatch]);

  // Device config
  const deviceConfig = useMemo(() => {
    return AUTOPLAY_POPUP_CONFIG.DEVICE_CONFIGS[device]?.[orientation] ||
           AUTOPLAY_POPUP_CONFIG.DEVICE_CONFIGS[PixiSettings.DEVICE.DESKTOP][PixiSettings.ORIENTATION.LANDSCAPE];
  }, [device, orientation]);

  const centerX = useMemo(() => dimensions.width / 2, [dimensions.width]);
  const centerY = useMemo(() => dimensions.height / 2, [dimensions.height]);

  // Textures
  const frameTexture = useMemo(() => getUITexture("popup_frame_vertical"), []);
  const closeTexture = useMemo(() => getUITexture("icon_close_menu"), []);
  const buttonTexture = useMemo(() => getUITexture("popup_button"), []);
  const radioActiveTexture = useMemo(() => getUITexture("popup_radio_button_active"), []);
  const radioInactiveTexture = useMemo(() => getUITexture("popup_radio_button"), []);
  const buttonAspectRatio = useMemo(
    () => buttonTexture ? buttonTexture.width / buttonTexture.height : 2.0,
    [buttonTexture]
  );

  // Effective popup scale — clamped so the frame never exceeds 90% of viewport in either dimension.
  // All inner elements (padding, fonts, buttons) derive from this, so everything scales together.
  const popupScale = useMemo(() => {
    const deviceScale = deviceConfig.scaleMultiplier || 1.0;
    if (!frameTexture) return deviceScale;

    // Compute the frame dimensions that this device scale would produce
    const idealContentWidth = AUTOPLAY_POPUP_CONFIG.baseContentWidth * deviceScale;
    const idealPadding = AUTOPLAY_POPUP_CONFIG.basePadding * deviceScale;
    const idealPanelWidth = idealContentWidth + idealPadding * 2;
    const idealFrameScale = idealPanelWidth / frameTexture.width;
    const idealFrameWidth = frameTexture.width * idealFrameScale;
    const idealFrameHeight = frameTexture.height * idealFrameScale;

    // Clamp against both viewport dimensions
    const maxWidth = dimensions.width * 0.8;
    const maxHeight = dimensions.height * 0.8;
    const widthRatio = idealFrameWidth > maxWidth ? maxWidth / idealFrameWidth : 1;
    const heightRatio = idealFrameHeight > maxHeight ? maxHeight / idealFrameHeight : 1;
    const clampRatio = Math.min(widthRatio, heightRatio);

    return deviceScale * clampRatio;
  }, [deviceConfig, frameTexture, dimensions.width, dimensions.height]);

  const padding = useMemo(() => AUTOPLAY_POPUP_CONFIG.basePadding * popupScale, [popupScale]);
  const titleFontSize = useMemo(() => Math.round(AUTOPLAY_POPUP_CONFIG.baseTitleFontSize * popupScale), [popupScale]);
  const contentWidth = useMemo(() => AUTOPLAY_POPUP_CONFIG.baseContentWidth * popupScale, [popupScale]);

  // Frame scaling — popupScale already accounts for viewport height
  const desiredPanelWidth = useMemo(() => contentWidth + padding * 2, [contentWidth, padding]);

  const frameScale = useMemo(() => {
    if (!frameTexture) return 1;
    return desiredPanelWidth / frameTexture.width;
  }, [frameTexture, desiredPanelWidth]);

  const frameSpriteWidth = useMemo(() => frameTexture ? frameTexture.width * frameScale : 0, [frameTexture, frameScale]);
  const frameSpriteHeight = useMemo(() => frameTexture ? frameTexture.height * frameScale : 0, [frameTexture, frameScale]);

  // Title bar area
  const titleBarHeight = useMemo(() => frameSpriteHeight * AUTOPLAY_POPUP_CONFIG.titleBarRatio, [frameSpriteHeight]);

  // Title style
  const titleText = useMemo(() => t("autoplay_title", { ns: "common" }).toUpperCase(), [t]);
  const titleStyle = useMemo(() => new PIXI.TextStyle({
    fontFamily: FONT_CONFIG.PRIMARY,
    fontSize: titleFontSize,
    fontWeight: "700",
    fill: 0xffbf11,
    align: "center",
  }), [titleFontSize]);

  // Content area config
  const viewportConfig = useMemo(() => {
    const contentAreaTop = -frameSpriteHeight / 2 + titleBarHeight;
    const contentAreaBottom = frameSpriteHeight / 2 - padding;
    const maxWidth = frameSpriteWidth - padding * 2;
      const extraTopGap = padding * 4.5
    const maxHeight = contentAreaBottom - contentAreaTop - padding * 2 - extraTopGap;
    return {
      maxWidth,
      maxHeight,
      x: -frameSpriteWidth / 2 + padding,
      y: contentAreaTop + padding * 2 + extraTopGap,
    };
  }, [frameSpriteWidth, frameSpriteHeight, padding, titleBarHeight]);

  // Button config — 3-column grid
  const autoplayButtonConfig = useMemo(() => {
    if (!viewportConfig) return { width: 100, height: 50, fontSize: 18, spacing: 10, rowSpacing: 10, columns: 3 };

    const columns = 3;
    const gapRatio = 0.075;
    const availableWidth = viewportConfig.maxWidth;
    const buttonWidth = availableWidth / (columns + (columns - 1) * gapRatio);
    const buttonHeight = buttonWidth / buttonAspectRatio;
    const spacing = buttonWidth * gapRatio;

    return {
      width: buttonWidth,
      height: buttonHeight,
      fontSize: Math.floor(buttonHeight * 0.5),
      spacing,
      rowSpacing: spacing * 2.3,
      columns,
    };
  }, [viewportConfig, buttonAspectRatio]);

  // Speed radio button config — circular, sized proportionally to autoplay buttons
  const speedRadioConfig = useMemo(() => {
    const circleSize = autoplayButtonConfig.height * 1.1;
    const labelFontSize = Math.floor(circleSize * 0.35);
    const labelGap = labelFontSize * 0.5;
    const totalItemHeight = labelFontSize + labelGap + circleSize;
    const spacing = autoplayButtonConfig.width + autoplayButtonConfig.spacing;
    return { circleSize, labelFontSize, labelGap, totalItemHeight, spacing };
  }, [autoplayButtonConfig]);

  // Speed label style
  const speedLabelStyle = useMemo(() => new PIXI.TextStyle({
    fontFamily: FONT_CONFIG.PRIMARY,
    fontSize: speedRadioConfig.labelFontSize,
    fontWeight: "600",
    fill: 0xffffff,
    align: "center",
  }), [speedRadioConfig.labelFontSize]);

  // Speed button positions — 3 evenly spaced
  const speedButtonPositions = useMemo(() => {
    const totalWidth = (SPEED_OPTIONS.length - 1) * speedRadioConfig.spacing;
    const startX = -totalWidth / 2;
    return SPEED_OPTIONS.map((_, index) => ({
      x: startX + index * speedRadioConfig.spacing,
      y: 0,
    }));
  }, [speedRadioConfig]);

  // Action button config (spin/stop at bottom)
  const actionButtonConfig = useMemo(() => {
    const width = autoplayButtonConfig.width;
    const height = autoplayButtonConfig.height;
    return { width, height, fontSize: autoplayButtonConfig.fontSize };
  }, [autoplayButtonConfig]);

  // Button positions — centered 3-column grid (positioned relative to grid container)
  const buttonPositions = useMemo(() => {
    return AUTOPLAY_OPTIONS.map((_, index) => {
      const row = Math.floor(index / autoplayButtonConfig.columns);
      const col = index % autoplayButtonConfig.columns;
      const totalWidth = (autoplayButtonConfig.columns - 1) * (autoplayButtonConfig.width + autoplayButtonConfig.spacing);
      const startX = -totalWidth / 2;
      const x = startX + col * (autoplayButtonConfig.width + autoplayButtonConfig.spacing);
      const y = row * (autoplayButtonConfig.height + autoplayButtonConfig.rowSpacing);
      return { x, y };
    });
  }, [autoplayButtonConfig]);

  // Calculate grid height
  const gridRows = Math.ceil(AUTOPLAY_OPTIONS.length / autoplayButtonConfig.columns);
  const totalGridHeight = gridRows * autoplayButtonConfig.height + (gridRows - 1) * autoplayButtonConfig.rowSpacing;

  // Action button anchored to the bottom of the content area
  const actionButtonY = useMemo(() => {
    return viewportConfig.maxHeight - actionButtonConfig.height / 2;
  }, [viewportConfig.maxHeight, actionButtonConfig.height]);

  // Grid positioned above the action button, with speed section above grid
  const speedSectionHeight = speedRadioConfig.totalItemHeight + autoplayButtonConfig.rowSpacing;

  const gridContainerY = useMemo(() => {
    const spaceAboveButton = viewportConfig.maxHeight - actionButtonConfig.height - autoplayButtonConfig.rowSpacing;
    const totalAboveContent = speedSectionHeight + totalGridHeight;
    const gridStartY = (spaceAboveButton - totalAboveContent) / 2 + speedSectionHeight;
    return gridStartY;
  }, [viewportConfig.maxHeight, actionButtonConfig.height, autoplayButtonConfig.rowSpacing, totalGridHeight, speedSectionHeight]);

  // Speed buttons positioned above the grid
  const speedContainerY = useMemo(() => {
    return gridContainerY - speedSectionHeight;
  }, [gridContainerY, speedSectionHeight]);

  // Overlay drawing
  const drawOverlay = useCallback((g) => {
    g.clear();
    g.beginFill(OVERLAY_BACKGROUND_COLOR);
    g.drawRect(0, 0, dimensions.width, dimensions.height);
    g.endFill();
  }, [dimensions.width, dimensions.height]);

  // Open animation
  useEffect(() => {
    if (isOpen && !isClosing) {
      setIsVisible(true);
      anime({
        targets: { alpha: 0 },
        alpha: OVERLAY_BACKGROUND_ALPHA,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
        complete: () => {
          if (popupContentRef.current) {
            popupContentRef.current.alpha = 0;
            popupContentRef.current.scale.set(0.85);
            anime({
              targets: popupContentRef.current,
              alpha: 1,
              duration: POPUP_ANIM_DURATION,
              easing: "easeOutQuad",
              complete: () => {
                setHasAnimated(true);
              },
            });
            anime({
              targets: popupContentRef.current.scale,
              x: 1,
              y: 1,
              duration: POPUP_ANIM_DURATION,
              easing: "easeOutQuad",
            });
          }
        },
      });
    } else if (!isOpen && !isClosing) {
      setIsVisible(false);
      setOverlayAlpha(0);
      setHasAnimated(false);
    }
  }, [isOpen, isClosing]);

  // Close animation
  useEffect(() => {
    if (!isClosing) return;
    let overlayAnim = null;
    let popupAnim = null;
    if (popupContentRef.current) {
      popupAnim = anime({
        targets: popupContentRef.current,
        alpha: 0,
        duration: POPUP_ANIM_DURATION,
        easing: "easeInQuad",
      });
      anime({
        targets: popupContentRef.current.scale,
        x: 0.85,
        y: 0.85,
        duration: POPUP_ANIM_DURATION,
        easing: "easeInQuad",
      });
    }
    overlayAnim = anime({
      targets: { alpha: OVERLAY_BACKGROUND_ALPHA },
      alpha: 0,
      duration: OVERLAY_FADE_DURATION,
      delay: POPUP_ANIM_DURATION,
      easing: "easeInQuad",
      update: (anim) => {
        setOverlayAlpha(anim.animations[0].currentValue);
      },
      complete: () => {
        setOverlayAlpha(0);
        dispatch(closeAutoplayPopup());
        setIsClosing(false);
        setIsVisible(false);
        setHasAnimated(false);
      },
    });
    return () => {
      if (overlayAnim) overlayAnim.pause();
      if (popupAnim) popupAnim.pause();
    };
  }, [isClosing, dispatch]);

  if (!isVisible && !isOpen) {
    return null;
  }

  return (
    <Container name="backport_autoplay_popup" zIndex={10000} sortableChildren={true}>
      {/* Overlay — click to close */}
      <Graphics
        draw={drawOverlay}
        alpha={overlayAlpha}
        eventMode="static"
        cursor="pointer"
        pointerdown={(e) => {
          e.stopPropagation();
          handleClose();
        }}
        zIndex={10000}
      />

      {/* Popup content */}
      <Container
        name="popup_content"
        ref={popupContentRef}
        zIndex={10001}
        x={centerX}
        y={centerY}
        alpha={hasAnimated ? 1 : 0}
        scale={hasAnimated ? 1 : 0.85}
      >
        {/* Frame background */}
        {frameTexture && (
          <Sprite
            texture={frameTexture}
            anchor={0.5}
            width={frameSpriteWidth}
            height={frameSpriteHeight}
          />
        )}

        {/* Close button — top right corner */}
        {closeTexture && (
          <Sprite
            texture={closeTexture}
            anchor={0.5}
            x={frameSpriteWidth / 2 - padding * 0.1}
            y={-frameSpriteHeight / 2 + padding * 0.1}
            width={titleFontSize * 1.8}
            height={titleFontSize * 1.8}
            eventMode="static"
            cursor="pointer"
            pointerdown={(e) => {
              e.stopPropagation();
              const btn = e.currentTarget;
              const origScaleX = btn.scale.x;
              const origScaleY = btn.scale.y;
              soundManager.play(SOUND_NAME.button_click);
              anime.remove(btn.scale);
              anime({
                targets: btn.scale,
                x: origScaleX * 0.85,
                y: origScaleY * 0.85,
                duration: 80,
                easing: "easeInQuad",
                complete: () => {
                  anime({
                    targets: btn.scale,
                    x: origScaleX,
                    y: origScaleY,
                    duration: 180,
                    easing: "easeOutQuad",
                  });
                },
              });
              handleClose();
            }}
          />
        )}

        {/* Title — positioned in the frame's built-in title bar area */}
        <Text
          text={titleText}
          style={titleStyle}
          anchor={0.5}
          x={0}
          y={-frameSpriteHeight / 2 + titleBarHeight / 2 + titleFontSize * 1.25}
          resolution={2}
        />

        {/* Content area */}
        <Container
          name="autoplay_content"
          x={viewportConfig.x + viewportConfig.maxWidth / 2}
          y={viewportConfig.y}
        >
          {/* Speed selection — radio buttons with labels */}
          <Container y={speedContainerY}>
            {SPEED_OPTIONS.map((option, index) => {
              const position = speedButtonPositions[index];
              const isSelected = currentSpeed === option.key;
              const texture = isSelected ? radioActiveTexture : radioInactiveTexture;

              return (
                <Container
                  key={option.key}
                  x={position.x}
                  y={position.y}
                  eventMode="static"
                  cursor="pointer"
                  accessible={true}
                  accessibleTitle={t(option.labelKey, { ns: "common" })}
                  pointerdown={(e) => {
                    // Press animation on the inner container (child[1]), not the sprite or label
                    const outerContainer = e.currentTarget;
                    const radioContainer = outerContainer?.children?.[1];
                    if (radioContainer) {
                      anime.remove(radioContainer.scale);
                      anime({
                        targets: radioContainer.scale,
                        x: 0.92,
                        y: 0.92,
                        duration: 65,
                        easing: "easeInQuad",
                        complete: () => {
                          anime({
                            targets: radioContainer.scale,
                            x: 1,
                            y: 1,
                            duration: 120,
                            easing: "easeOutBack",
                          });
                        },
                      });
                    }
                    handleSpeedChange(option.key);
                  }}
                >
                  {/* Label above — not animated */}
                  <Text
                    text={t(option.labelKey, { ns: "common" }).toUpperCase()}
                    style={speedLabelStyle}
                    anchor={0.5}
                    x={0}
                    y={-speedRadioConfig.circleSize / 2 - speedRadioConfig.labelGap}
                    resolution={2}
                  />
                  {/* Animation container — press animates this, sprite inside keeps React scale */}
                  <Container
                    x={0}
                    y={speedRadioConfig.labelFontSize / 2}
                  >
                    {texture && (
                      <Sprite
                        texture={texture}
                        anchor={0.5}
                        width={speedRadioConfig.circleSize}
                        height={speedRadioConfig.circleSize}
                      />
                    )}
                  </Container>
                </Container>
              );
            })}
          </Container>

          {/* Autoplay count buttons — 3-column grid, below speed section */}
          <Container y={gridContainerY}>
            {AUTOPLAY_OPTIONS.map((option, index) => {
              const position = buttonPositions[index];
              const isSelected = autoSpinCount === option;
              const isDisabled = autoPlayActive;

              return (
                <PopupButton
                  key={option === Infinity ? "infinity" : option}
                  x={position.x}
                  y={position.y}
                  text={option === Infinity ? "∞" : option.toString()}
                  onClick={() => handleAutoSpinCountSelect(option)}
                  disabled={isDisabled}
                  active={isSelected}
                  width={autoplayButtonConfig.width}
                  height={autoplayButtonConfig.height}
                  fontSize={autoplayButtonConfig.fontSize}
                  accessibleTitle={option === Infinity ? t("autoplay_title", { ns: "common" }) : `${option} ${t("autoplay_title", { ns: "common" })}`}
                />
              );
            })}
          </Container>

          {/* Action button (Spin / Stop) — anchored to bottom */}
          <PopupButton
            x={0}
            y={actionButtonY}
            text={actionButtonText}
            onClick={handleActionClick}
            disabled={false}
            active={false}
            width={actionButtonConfig.width}
            height={actionButtonConfig.height}
            fontSize={actionButtonConfig.fontSize}
            accessibleTitle={actionButtonText}
          />
        </Container>
      </Container>
    </Container>
  );
};

export default AutoplayPopup;
